// Stage management constants and utilities
import { isValidAdminCharge } from "../constants/adminCharge";
import { hasRichTextContent } from "./richTextUtils";

export const MAX_STAGE = 9;
export const EVENT_MAX_STAGE = 7;
export const ATTRACTION_MAX_STAGE = 6;

//Attraction stages
export const ATTRACTION_STAGES = {
  GENERAL: 1,
  TICKETS: 2,
  PRICING: 3,
  INCLUSIONS: 4,
  FAQ: 5,
  TERMS: 6
};

//Attraction stage IDs
export const ATTRACTION_STAGE_IDS = {
  1: 'general',
  2: 'tickets',
  3: 'pricing',
  4: 'inclusions',
  5: 'faq',
  6: 'terms'
};

// Package stages
export const STAGES = {
  GENERAL: 1,
  ITINERARY: 2,
  STAY: 3,
  INCLUSION: 4,
  PRICING: 5,
  ADDITIONAL: 6,
  TERMS: 7,
  CANCELLATION: 8,
  FAQ: 9
};

export const MIN_PACKAGE_IMAGES = 5;

export const GENERAL_INFO_FIELD_ORDER = [
  "name",
  "tour_type",
  "minimum_members",
  "maximum_members",
  "suitable_ids",
  "package_activities",
  "starting_location",
  "pickup_point",
  "pickup_time",
  "dropoff_point",
  "dropoff_time",
  "total_days",
  "package_days_each_destinations",
  "about",
  "images",
];

export const GENERAL_INFO_FIELD_ANCHOR_IDS = {
  name: "package-general-name",
  tour_type: "package-general-tour_type",
  minimum_members: "package-general-minimum_members",
  maximum_members: "package-general-maximum_members",
  suitable_ids: "package-general-suitable_ids",
  package_activities: "package-general-package_activities",
  starting_location: "package-general-starting_location",
  pickup_point: "package-general-pickup_point",
  pickup_time: "package-general-pickup_time",
  dropoff_point: "package-general-dropoff_point",
  dropoff_time: "package-general-dropoff_time",
  total_days: "package-general-total_days",
  package_days_each_destinations: "package-general-package_days_each_destinations",
  about: "package-general-about",
  images: "package-images",
};

const stripHtml = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

const isBlank = (value) =>
  value === "" || value === null || value === undefined;

const getFirstGeneralInfoError = (errors) => {
  const field = GENERAL_INFO_FIELD_ORDER.find((key) => errors[key]);
  if (!field) {
    return { field: null, message: null };
  }

  const message = Array.isArray(errors[field]) ? errors[field][0] : errors[field];
  return { field, message };
};

export const scrollToGeneralInfoField = (field) => {
  if (!field) return;

  const anchorId = GENERAL_INFO_FIELD_ANCHOR_IDS[field];
  if (!anchorId) return;

  document
    .getElementById(anchorId)
    ?.scrollIntoView({ behavior: "smooth", block: "center" });
};

export const validateGeneralInfoStage = (
  data,
  { isUpdate = false, isExploreWorldSupplier } = {}
) => {
  const errors = {};
  const tourType = data?.tour_type;
  const isFixedDeparture = tourType === "fixed_departure";
  const canCreatePrivate =
    typeof isExploreWorldSupplier === "boolean"
      ? isExploreWorldSupplier
      : (() => {
          try {
            const user = JSON.parse(localStorage.getItem("user") || "null");
            return Boolean(
              user?.supplier_details?.is_exploreworld ??
                user?.supplierDetails?.is_exploreworld
            );
          } catch {
            return false;
          }
        })();

  const addError = (field, message) => {
    if (!errors[field]) {
      errors[field] = [message];
    }
  };

  if (!String(data?.name || "").trim()) {
    addError("name", "Package name is required.");
  }

  if (!tourType) {
    addError("tour_type", "Tour type is required.");
  } else if (tourType === "private" && !canCreatePrivate) {
    addError(
      "tour_type",
      "Only ExploreWorld suppliers can create Private Tour packages."
    );
  } else if (!["private", "fixed_departure"].includes(tourType)) {
    addError("tour_type", "Invalid tour type.");
  }

  if (isBlank(data?.minimum_members)) {
    addError("minimum_members", "Minimum members is required.");
  }

  if (isBlank(data?.maximum_members)) {
    addError("maximum_members", "Maximum members is required.");
  } else if (Number(data.maximum_members) < 1) {
    addError("maximum_members", "Maximum members must be at least 1.");
  }

  const suitableIds = Array.isArray(data?.suitable_ids) ? data.suitable_ids : [];
  if (suitableIds.length === 0) {
    addError("suitable_ids", "Please select at least one suitable for option.");
  }

  const activities = Array.isArray(data?.package_activities)
    ? data.package_activities
    : [];
  if (activities.length === 0) {
    addError("package_activities", "Please select at least one activity.");
  }

  if (isFixedDeparture) {
    if (!String(data?.starting_location || "").trim()) {
      addError("starting_location", "Starting location is required.");
    }
    if (!String(data?.pickup_point || "").trim()) {
      addError("pickup_point", "Pickup point is required.");
    }
    if (!data?.pickup_time) {
      addError("pickup_time", "Pickup time is required.");
    }
    if (!String(data?.dropoff_point || "").trim()) {
      addError("dropoff_point", "Dropoff point is required.");
    }
    if (!data?.dropoff_time) {
      addError("dropoff_time", "Dropoff time is required.");
    }
  }

  if (isBlank(data?.total_days) || Number(data.total_days) < 1) {
    addError("total_days", "Total days is required.");
  }

  const destinations = Array.isArray(data?.package_days_each_destinations)
    ? data.package_days_each_destinations
    : [];
  const hasValidDestination = destinations.some(
    (destination) =>
      String(destination?.location || "").trim() &&
      Number(destination?.no_of_days) > 0
  );
  if (!hasValidDestination) {
    addError(
      "package_days_each_destinations",
      "Please add at least one destination with number of days."
    );
  }

  if (!stripHtml(data?.about)) {
    addError("about", "Overview is required.");
  }

  const imageCount = Array.isArray(data?.images) ? data.images.length : 0;
  if (!isUpdate && imageCount < MIN_PACKAGE_IMAGES) {
    addError(
      "images",
      imageCount === 0
        ? `Please upload at least ${MIN_PACKAGE_IMAGES} package images.`
        : `Please upload at least ${MIN_PACKAGE_IMAGES} package images. You have uploaded ${imageCount}.`
    );
  }

  const { field: firstErrorField, message: firstErrorMessage } =
    getFirstGeneralInfoError(errors);

  return {
    valid: Object.keys(errors).length === 0,
    errors,
    firstErrorField,
    firstErrorMessage,
  };
};

// Event stages
export const EVENT_STAGES = {
  GENERAL: 1,
  TICKETS: 2,
  PRICING: 3,
  HIGHLIGHTS: 4,
  FAQ: 5,
  TERMS: 6,
  CANCELLATION: 7
};

// Package stage IDs
export const STAGE_IDS = {
  1: 'general',
  2: 'itinerary', 
  3: 'stay',
  4: 'inclusion',
  5: 'pricing',
  6: 'additional',
  7: 'terms',
  8: 'cancellation',
  9: 'faq'
};

// Event stage IDs
export const EVENT_STAGE_IDS = {
  1: 'general',
  2: 'tickets',
  3: 'pricing',
  4: 'highlights',
  5: 'faq',
  6: 'terms',
  7: 'cancellation'
};

// Stage validation rules for packages
export const isItineraryDaySaveable = (day) => {
  const destination = String(day?.destination || "").trim();
  return Boolean(destination && hasRichTextContent(day?.description));
};

export const stageValidationRules = {
  [STAGES.GENERAL]: (data) => validateGeneralInfoStage(data).valid,
  [STAGES.ITINERARY]: (data) => {
    if (!Array.isArray(data?.days) || data.days.length === 0) return false;
    return data.days.some(
      (day) => Number(day.is_completed) === 1 || isItineraryDaySaveable(day)
    );
  },
  [STAGES.STAY]: (data) => {
    return data?.hasValidStays;
  },
  [STAGES.INCLUSION]: (data) => {
    if (data?.masterHighlightsAvailable === false) {
      return (
        (data?.detailed_inclusions?.length > 0) ||
        (data?.exclusions?.length > 0) ||
        (data?.things_to_carry?.length > 0)
      );
    }
    return data?.inclusion_highlights?.length > 0;
  },
  [STAGES.PRICING]: (data) => {
    // Check if pricing data exists and has required structure
    if (!data) return false;
    
    // Check if payment_terms exists and has at least one key
    if (!data.payment_terms || Object.keys(data.payment_terms).length === 0) {
      return false;
    }
    
    // Check if categories exist and at least one category has base price or rates
    if (!data.categories || !Array.isArray(data.categories) || data.categories.length === 0) {
      return false;
    }
    
    const categoryHasBasePrice = (category) => {
      const bp = category.base_price;
      if (!bp) return Boolean(category.is_base_price_set);

      const adultAdminFee = bp.adult?.adminFee;
      const childAdminFee = bp.child?.adminFee || adultAdminFee;

      return (
        bp.adult?.basePrice &&
        adultAdminFee &&
        bp.child?.basePrice &&
        childAdminFee
      );
    };

    return data.categories.every(categoryHasBasePrice);
  },
  [STAGES.ADDITIONAL]: (data) => {
    return data?.additional_info?.trim().length > 0;
  },
  [STAGES.TERMS]: (data) => {
    return data?.terms_and_conditions?.trim().length > 0;
  },
  [STAGES.CANCELLATION]: (data) => {
    if (!data?.data?.length) return false;
    return data.data.some((rule) => {
      const refund = rule.refund_percentage;
      const hasRefund =
        refund !== "" && refund !== null && refund !== undefined;
      if (!hasRefund) return false;
      if (rule.days_type === "days_range") {
        return (
          Boolean(rule.cancellation_days_from) &&
          Boolean(rule.cancellation_days_to)
        );
      }
      return Boolean(rule.cancellation_days);
    });
  },
  [STAGES.FAQ]: (data) => {
    return data && Object.keys(data).length > 0;
  }
};

// Stage validation rules for attractions
export const attractionStageValidationRules = {
  [ATTRACTION_STAGES.GENERAL]: (data) => {
    return data &&
           data.name &&
           data.name.trim().length > 0 &&
           data.category &&
           data.chargable_from !== "" &&
           data.chargable_from !== null &&
           data.chargable_from !== undefined &&
           hasRichTextContent(data.description) &&
           data.location?.address &&
           data.location.address.trim().length > 0 &&
           data.latitude &&
           data.longitude &&
           String(data.start_time ?? "").trim() &&
           String(data.end_time ?? "").trim() &&
           String(data.address ?? data.location?.address ?? "").trim();
  },
  [ATTRACTION_STAGES.TICKETS]: (data) => {
    const tickets = data?.tickets ?? data?.data ?? data;
    if (!Array.isArray(tickets) || tickets.length === 0) return false;
    return tickets.some((ticket) => String(ticket?.name ?? "").trim());
  },
  [ATTRACTION_STAGES.PRICING]: (data) => {
    // Check if we have pricing data with regularTickets
    if (!data || !data.regularTickets || !Array.isArray(data.regularTickets)) {
      return false;
    }
    
    const isPaxMode = data.pricingMode === "pax";

    // Check if at least one ticket has valid required data
    return data.regularTickets.some(ticket =>
      ticket.price > 0 &&
      isValidAdminCharge(ticket.adminCharge) &&
      hasRichTextContent(ticket.description) &&
      ticket.maximumAllowedBookingsPerUser > 0 &&
      (!isPaxMode || (ticket.childPrice !== "" && ticket.childPrice !== null && ticket.childPrice !== undefined && Number(ticket.childPrice) >= 0))
    );
  },
  [ATTRACTION_STAGES.FAQ]: (data) => {
    if (!data?.faqs?.length) return false;
    return data.faqs.some(
      (faq) =>
        String(faq?.question ?? "").trim() &&
        String(faq?.answer ?? "").trim()
    );
  },
  [ATTRACTION_STAGES.INCLUSIONS]: () => true,
  [ATTRACTION_STAGES.TERMS]: (data) => {
    return hasRichTextContent(data?.description);
  }
};

// Stage validation rules for events
export const eventStageValidationRules = {
  [EVENT_STAGES.GENERAL]: (data) => {
    return data && Object.keys(data).length > 0;
  },
  [EVENT_STAGES.PRICING]: (data) => {
    return data && Object.keys(data).length > 0;
  },
  [EVENT_STAGES.HIGHLIGHTS]: (data) => {
    return data?.event_highlights?.length > 0;
  },
  [EVENT_STAGES.FAQ]: (data) => {
    return data && Object.keys(data).length > 0;
  },
  [EVENT_STAGES.TERMS]: (data) => {
    return data && Object.keys(data).length > 0;
  },
  [EVENT_STAGES.CANCELLATION]: (data) => {
    return data && Object.keys(data).length > 0;
  }
};

// Attraction stage helpers
export const isAttractionFreeBooking = (generalInfoData) =>
  Boolean(generalInfoData?.free_booking);

export const getAttractionNextStage = (currentStage, isFreeBooking = false) => {
  let next = Math.min(Number(currentStage) + 1, ATTRACTION_MAX_STAGE);
  if (isFreeBooking && next === ATTRACTION_STAGES.PRICING) {
    next = ATTRACTION_STAGES.INCLUSIONS;
  }
  return next;
};

export const getEffectiveAttractionCompletedStage = (
  savedStage,
  isFreeBooking = false
) => {
  const stage = Number(savedStage) || 1;
  if (isFreeBooking && stage >= ATTRACTION_STAGES.PRICING) {
    return Math.max(stage, ATTRACTION_STAGES.INCLUSIONS);
  }
  return stage;
};

export const isAttractionStageAccessible = (targetStage, currentStage, completedStage) => {
  // In create mode, only allow access to stages up to the completed stage
  // completedStage is the highest stage that has been saved
  const highestAccessibleStage = completedStage || 1;
  return targetStage <= highestAccessibleStage;
};

export const validateAttractionStage = (stage, data) => {
  const validationRule = attractionStageValidationRules[stage];
  return validationRule ? validationRule(data) : true;
};

// Package stage helpers
export const getNextStage = (currentStage, isEvent = false) => {
  return Math.min(currentStage + 1, isEvent ? EVENT_MAX_STAGE : MAX_STAGE);
};

export const isStageAccessible = (targetStage, currentStage, completedStage) => {
  return targetStage <= currentStage || targetStage <= completedStage;
};

export const validateStage = (stage, data, isEvent = false) => {
  const validationRule = isEvent ? eventStageValidationRules[stage] : stageValidationRules[stage];
  return validationRule ? validationRule(data) : true;
};

// Package stage data getter
export const getStageData = (stage, {
  generalInfoData,
  itineraryData,
  stayData,
  inclusionData,
  pricingData,
  additionalInfoData,
  termsConditionData,
  cancellationData,
  faqData
}) => {
  const stageDataMap = {
    [STAGES.GENERAL]: generalInfoData,
    [STAGES.ITINERARY]: itineraryData,
    [STAGES.STAY]: stayData,
    [STAGES.INCLUSION]: inclusionData,
    [STAGES.PRICING]: pricingData,
    [STAGES.ADDITIONAL]: additionalInfoData,
    [STAGES.TERMS]: termsConditionData,
    [STAGES.CANCELLATION]: cancellationData,
    [STAGES.FAQ]: faqData
  };
  
  return stageDataMap[stage];
};

// Attraction stage data getter
export const getAttractionStageData = (stage, {
  generalInfoData,
  pricingData,
  faqData,
  inclusionExclusionData,
  termsConditionData,
  ticketsData,
}) => {
  const stageDataMap = {
    [ATTRACTION_STAGES.GENERAL]: generalInfoData,
    [ATTRACTION_STAGES.TICKETS]: { tickets: ticketsData || [] },
    [ATTRACTION_STAGES.PRICING]: pricingData,
    [ATTRACTION_STAGES.FAQ]: faqData,
    [ATTRACTION_STAGES.INCLUSIONS]: inclusionExclusionData,
    [ATTRACTION_STAGES.TERMS]: termsConditionData,
  };

  return stageDataMap[stage];
};

// Event stage data getter
export const getEventStageData = (stage, {
  generalInfoData,
  pricingData,
  termsConditionData,
  faqData,
  cancellationData
}) => {
  const stageDataMap = {
    [EVENT_STAGES.GENERAL]: generalInfoData,
    [EVENT_STAGES.TICKETS]: generalInfoData,
    [EVENT_STAGES.PRICING]: pricingData,
    [EVENT_STAGES.FAQ]: faqData,
    [EVENT_STAGES.TERMS]: termsConditionData,
    [EVENT_STAGES.CANCELLATION]: cancellationData
  };
  
  return stageDataMap[stage];
};

export const isEventFreeBooking = (generalInfoData) =>
  Boolean(generalInfoData?.free_booking);

export const getEventNextStage = (currentStage, isFreeBooking = false) => {
  let next = Math.min(Number(currentStage) + 1, EVENT_MAX_STAGE);
  if (isFreeBooking && next === EVENT_STAGES.PRICING) {
    next = EVENT_STAGES.HIGHLIGHTS;
  }
  return next;
};

export const getEffectiveEventCompletedStage = (savedStage, isFreeBooking = false) => {
  const stage = Number(savedStage) || 1;
  if (isFreeBooking && stage >= EVENT_STAGES.PRICING) {
    return Math.max(stage, EVENT_STAGES.HIGHLIGHTS);
  }
  return stage;
};